/**
 * The browser's right-click menu, suppressed for the whole app.
 *
 * This is a game, not a document: "Save image as…" over the sky is never what
 * was meant, and since 2026-08-25 the right button is also a *control* — hold
 * it and drag and the pilot looks around (`flight/free-look.ts`). A native menu
 * popping on the press takes the pointer, so the `pointerup` that ends the drag
 * never arrives and the head stays looking over its shoulder.
 *
 * ## Why this is a module and not the one line it was
 *
 * It was one line at the bottom of `index.tsx` (`88ddf9a`), and the user still
 * saw the menu. Three things were wrong with it, and only the first is a bug in
 * the ordinary sense:
 *
 * 1. **It listened in the bubble phase.** A `preventDefault()` on `document`
 *    only ever runs if the event reaches `document`, and anything between the
 *    target and the root may call `stopPropagation()` first. That is not
 *    hypothetical here: `flight/PreferencesOverlay.tsx` already stops
 *    propagation on the keys it owns, on exactly the reasoning that a
 *    full-screen surface owns its own input — the next overlay to do the same
 *    for pointer or menu events silently unsuppresses the menu over itself, and
 *    nothing in the suppression would mention the overlay or vice versa. In the
 *    **capture** phase the root sees the event first, so no descendant can take
 *    it away. That is the level that cannot rot.
 *
 * 2. **It installed late.** The line sat after `registerServiceWorker()`, which
 *    does real work at import time — an `?unstale` recovery that can navigate,
 *    and a boot heartbeat. Anything that throws in there aborts the module and
 *    the suppression never installs at all. It is now the first statement in
 *    `index.tsx`, before the router, before the service worker, before
 *    anything that can fail.
 *
 * 3. **It was untestable.** A listener installed by a side effect at the bottom
 *    of the app entry point cannot be driven by a test, so nothing could say
 *    whether it covered the surfaces that actually exist. `context-menu.test.ts`
 *    walks `keys.ts`'s own `UI_OVERLAY_CLASSES` — the port's real registry of
 *    everything that sits over the viewport — the way `shell-flow.test.ts`
 *    walks the real menu tables, and dispatches a menu event at each.
 *
 * The suppression is deliberately unconditional: there is no surface in this
 * app where the browser's menu is the right answer. There is no text to copy
 * (every screen is a canvas or a canvas-styled overlay), no link worth opening
 * in a new tab, and no form field — the one place a browser menu earns its
 * keep, and the one thing `/` has never had, since the pilot's name is typed
 * into a `RB.EXE` prompt drawn on a canvas.
 */

/** The slice of `Document` this needs — so a test can pass a fake one, and so nothing here depends on a real DOM. */
export interface ContextMenuHost {
  addEventListener(type: "contextmenu", listener: (event: Event) => void, options?: { capture?: boolean }): void;
  removeEventListener(type: "contextmenu", listener: (event: Event) => void, options?: { capture?: boolean }): void;
}

/** Capture, not bubble — reason 1 in the module doc. Exported so the test asserts the phase rather than trusting it. */
export const CONTEXT_MENU_LISTENER_OPTIONS = { capture: true } as const;

function suppress(event: Event): void {
  event.preventDefault();
}

/**
 * Installs the suppression on `host` and returns the undo.
 *
 * The undo exists for the test, not for the app: `/` never wants the menu back,
 * and `index.tsx` drops the returned function on purpose.
 */
export function installContextMenuSuppression(host: ContextMenuHost): () => void {
  host.addEventListener("contextmenu", suppress, CONTEXT_MENU_LISTENER_OPTIONS);
  return () => host.removeEventListener("contextmenu", suppress, CONTEXT_MENU_LISTENER_OPTIONS);
}
